import { ArrowDownRight, ArrowRight, ArrowUpRight } from "lucide-react";
import * as React from "react";
import { cx } from "../internal/cx.js";

export type MetricCardTrend = "up" | "down" | "flat" | "none";
export type MetricCardTone = "neutral" | "positive" | "negative";
/** Color of the icon tile — a colored glyph on a matching lighter background. */
export type MetricCardIconTone = "blue" | "green" | "coral" | "sunny" | "neutral";

export interface MetricCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Short noun phrase ("Active employees", "Revenue"). */
  label: React.ReactNode;
  /** The headline number — already formatted by the parent. */
  value: React.ReactNode;
  /** Optional change text ("+8.2%", "-12"). */
  delta?: React.ReactNode;
  /**
   * Trend tone for the delta chip. Default 'none' (no color, no icon).
   * 'up' / 'down' read as good/bad in the default tone — pass `tone` to
   * invert for metrics where decreases are positive (e.g. churn).
   */
  trend?: MetricCardTrend;
  /**
   * Override the semantic interpretation of trend.
   * Default: 'up' = positive, 'down' = negative, 'flat' = neutral.
   * Pass 'negative' for an upward trend that is bad (e.g. costs).
   */
  tone?: MetricCardTone;
  /** Description shown under the value (the period of comparison). */
  description?: React.ReactNode;
  /** Leading visual (lucide icon recommended). */
  icon?: React.ReactNode;
  /**
   * Color of the icon tile (glyph + matching lighter background).
   * Default 'blue'. Use to give the tile a semantic hue (e.g. 'green' for a
   * "good" metric, 'coral' for one that needs attention).
   */
  iconTone?: MetricCardIconTone;
  /** Footer slot (CTA, sparkline, supporting text). */
  footer?: React.ReactNode;
  /** Visually emphasize the card. */
  elevated?: boolean;
}

function toneForTrend(trend: MetricCardTrend, override?: MetricCardTone): MetricCardTone {
  if (override) return override;
  if (trend === "up") return "positive";
  if (trend === "down") return "negative";
  return "neutral";
}

/**
 * Dashboard KPI tile. Primary slot is a single (already-formatted) number;
 * secondary slots are an optional delta chip with auto-coloring trend icon,
 * a description, an icon, and a footer.
 */
export const MetricCard = React.forwardRef<HTMLDivElement, MetricCardProps>(function MetricCard(
  {
    className,
    delta,
    description,
    elevated,
    footer,
    icon,
    iconTone = "blue",
    label,
    tone,
    trend = "none",
    value,
    ...props
  },
  ref,
) {
  const resolvedTone = toneForTrend(trend, tone);
  const TrendIcon =
    trend === "up"
      ? ArrowUpRight
      : trend === "down"
        ? ArrowDownRight
        : trend === "flat"
          ? ArrowRight
          : null;

  return (
    <div
      ref={ref}
      className={cx("cw-v2-metric-card", elevated && "cw-v2-metric-card--elevated", className)}
      {...props}
    >
      <div className="cw-v2-metric-card__header">
        <div className="cw-v2-metric-card__label">{label}</div>
        {icon ? (
          <div
            aria-hidden
            className={cx("cw-v2-metric-card__icon", `cw-v2-metric-card__icon--${iconTone}`)}
          >
            {icon}
          </div>
        ) : null}
      </div>
      <div className="cw-v2-metric-card__value-row">
        <div className="cw-v2-metric-card__value">{value}</div>
        {delta !== undefined && delta !== null ? (
          <span
            className={cx("cw-v2-metric-card__delta", `cw-v2-metric-card__delta--${resolvedTone}`)}
          >
            {TrendIcon ? <TrendIcon aria-hidden className="cw-v2-metric-card__delta-icon" /> : null}
            {delta}
          </span>
        ) : null}
      </div>
      {description ? <div className="cw-v2-metric-card__description">{description}</div> : null}
      {footer ? <div className="cw-v2-metric-card__footer">{footer}</div> : null}
    </div>
  );
});
MetricCard.displayName = "MetricCard";
